
import { create } from 'zustand';
import type { CartItem, Customer, Product, Discount, TaxOverride } from '../types';

interface CartState {
  items: CartItem[];
  customer: Customer | null;
  orderDiscount: Discount | null;
  taxOverrides: TaxOverride[];
  roundNumber: number;
  isLocked: boolean;
  notes: string;
  
  // Computed values
  subtotal: number;
  itemDiscountTotal: number;
  orderDiscountAmount: number;
  totalDiscountAmount: number;
  taxAmount: number;
  total: number;
  
  // Actions
  addItem: (product: Product, quantity?: number) => void;
  removeItem: (itemId: string) => void;
  updateQuantity: (itemId: string, quantity: number) => void;
  updateItemDiscount: (itemId: string, discount: Discount) => void;
  setCustomer: (customer: Customer | null) => void;
  setOrderDiscount: (discount: Discount | null) => void;
  setTaxOverride: (itemId: string, newRate: number, reason: string, authorizedBy: string) => void;
  removeTaxOverride: (itemId: string) => void;
  clearCart: () => void;
  voidItem: (itemId: string, reason?: string) => void;
  confirmItem: (itemId: string) => void;
  incrementRound: () => void;
  lockOrder: () => void;
  unlockOrder: () => void;
  setNotes: (notes: string) => void;
  calculateTotals: () => void;
  splitQuantity: (itemId: string, splitQty: number) => void;
  mergeItems: (sourceId: string, targetId: string) => void;
}

export const useCartStore = create<CartState>((set, get) => ({
  items: [],
  customer: null,
  orderDiscount: null,
  taxOverrides: [],
  roundNumber: 1,
  isLocked: false,
  notes: '',
  subtotal: 0,
  itemDiscountTotal: 0,
  orderDiscountAmount: 0,
  totalDiscountAmount: 0,
  taxAmount: 0,
  total: 0,

  addItem: (product, quantity = 1) => {
    const { items, roundNumber, isLocked } = get();
    
    if (isLocked) {
      console.warn('Order is locked');
      return;
    }

    // Check if item already exists in current round
    const existingItem = items.find(
      item => item.product_id === product.id && 
      item.round_number === roundNumber && 
      !item.is_canceled
    );

    if (existingItem) {
      get().updateQuantity(existingItem.id, existingItem.quantity + quantity);
    } else {
      const newItem: CartItem = {
        id: `temp-${Date.now()}-${Math.random()}`,
        order_id: '',
        product_id: product.id,
        product,
        productName: product.name,
        categoryName: product.category?.name,
        imageUrl: product.image_url || undefined,
        quantity,
        unit_price: product.price,
        originalPrice: product.price,
        discount_amount: 0,
        discount_percent: 0,
        tax_rate: product.tax_rate,
        tax_amount: 0,
        total: 0,
        round_number: roundNumber,
        is_confirmed: false,
        is_canceled: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      set({ items: [...items, newItem] });
    }
    
    get().calculateTotals();
  },

  removeItem: (itemId) => {
    const { isLocked } = get();
    if (isLocked) return;
    
    set(state => ({
      items: state.items.filter(item => item.id !== itemId),
      taxOverrides: state.taxOverrides.filter(override => override.itemId !== itemId)
    }));
    get().calculateTotals();
  },

  updateQuantity: (itemId, quantity) => {
    const { isLocked } = get();
    if (isLocked) return;
    
    if (quantity <= 0) {
      get().removeItem(itemId);
      return;
    }

    set(state => ({
      items: state.items.map(item =>
        item.id === itemId
          ? { ...item, quantity, updated_at: new Date().toISOString() }
          : item
      )
    }));
    get().calculateTotals();
  },

  updateItemDiscount: (itemId, discount) => {
    const { isLocked } = get();
    if (isLocked) return;
    
    set(state => ({
      items: state.items.map(item =>
        item.id === itemId
          ? {
              ...item,
              discount_amount: discount.type === 'fixed' ? discount.value : 0,
              discount_percent: discount.type === 'percent' ? discount.value : 0,
              discountDescription: discount.reason,
              updated_at: new Date().toISOString()
            }
          : item
      )
    }));
    get().calculateTotals();
  },

  setCustomer: (customer) => {
    set({ customer });
    get().calculateTotals();
  },

  setOrderDiscount: (discount) => {
    const { isLocked } = get();
    if (isLocked) return;
    
    set({ orderDiscount: discount });
    get().calculateTotals();
  },

  setTaxOverride: (itemId, newRate, reason, authorizedBy) => {
    const { items, taxOverrides } = get();
    const item = items.find(i => i.id === itemId);
    
    if (!item) return;

    const existingOverride = taxOverrides.find(o => o.itemId === itemId);
    
    if (existingOverride) {
      set({
        taxOverrides: taxOverrides.map(o =>
          o.itemId === itemId
            ? { ...o, overrideRate: newRate, reason, authorizedBy }
            : o
        )
      });
    } else {
      set({
        taxOverrides: [
          ...taxOverrides,
          {
            itemId,
            originalRate: item.tax_rate,
            overrideRate: newRate,
            reason,
            authorizedBy
          }
        ]
      });
    }
    
    get().calculateTotals();
  },

  removeTaxOverride: (itemId) => {
    set(state => ({
      taxOverrides: state.taxOverrides.filter(o => o.itemId !== itemId)
    }));
    get().calculateTotals();
  },

  clearCart: () => {
    set({
      items: [],
      customer: null,
      orderDiscount: null,
      taxOverrides: [],
      roundNumber: 1,
      isLocked: false,
      notes: '',
      subtotal: 0,
      itemDiscountTotal: 0,
      orderDiscountAmount: 0,
      totalDiscountAmount: 0,
      taxAmount: 0,
      total: 0,
    });
  },

  voidItem: (itemId, reason) => {
    set(state => ({
      items: state.items.map(item =>
        item.id === itemId
          ? { ...item, is_canceled: true, notes: reason, updated_at: new Date().toISOString() }
          : item
      )
    }));
    get().calculateTotals();
  },

  confirmItem: (itemId) => {
    set(state => ({
      items: state.items.map(item =>
        item.id === itemId
          ? { ...item, is_confirmed: true, updated_at: new Date().toISOString() }
          : item
      )
    }));
  },

  incrementRound: () => {
    set(state => ({ roundNumber: state.roundNumber + 1 }));
  },

  lockOrder: () => {
    set({ isLocked: true });
  },

  unlockOrder: () => {
    set({ isLocked: false });
  },

  setNotes: (notes) => {
    set({ notes });
  },

  splitQuantity: (itemId, splitQty) => {
    const { items } = get();
    const item = items.find(i => i.id === itemId);
    
    if (!item || splitQty >= item.quantity || splitQty <= 0) return;

    const newItem: CartItem = {
      ...item,
      id: `temp-${Date.now()}-${Math.random()}`,
      quantity: splitQty,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    set(state => ({
      items: [
        ...state.items.map(i =>
          i.id === itemId
            ? { ...i, quantity: i.quantity - splitQty }
            : i
        ),
        newItem
      ]
    }));
    
    get().calculateTotals();
  },

  mergeItems: (sourceId, targetId) => {
    const { items } = get();
    const source = items.find(i => i.id === sourceId);
    const target = items.find(i => i.id === targetId);
    
    if (!source || !target || source.product_id !== target.product_id) return;

    set(state => ({
      items: state.items
        .filter(i => i.id !== sourceId)
        .map(i =>
          i.id === targetId
            ? { ...i, quantity: i.quantity + source.quantity }
            : i
        )
    }));
    
    get().calculateTotals();
  },

  calculateTotals: () => {
    const { items, customer, orderDiscount, taxOverrides } = get();
    
    let subtotal = 0;
    let itemDiscountTotal = 0;
    let totalTax = 0;

    // Calculate item-level totals
    const updatedItems = items.map(item => {
      if (item.is_canceled) {
        return { ...item, total: 0, tax_amount: 0 };
      }

      const itemSubtotal = item.unit_price * item.quantity;
      
      // Calculate item discount
      let itemDiscount = 0;
      if (item.discount_percent > 0) {
        itemDiscount = itemSubtotal * (item.discount_percent / 100);
      } else if (item.discount_amount > 0) {
        itemDiscount = Math.min(item.discount_amount, itemSubtotal);
      }

      // Apply customer discount if no item discount
      if (customer?.discount_rate && !itemDiscount) {
        itemDiscount = itemSubtotal * (customer.discount_rate / 100);
      }

      itemDiscountTotal += itemDiscount;
      const subtotalAfterDiscount = itemSubtotal - itemDiscount;

      // Get tax rate (check for override)
      const taxOverride = taxOverrides.find(o => o.itemId === item.id);
      const effectiveTaxRate = taxOverride ? taxOverride.overrideRate : item.tax_rate;
      
      // Calculate tax
      const taxAmount = subtotalAfterDiscount * (effectiveTaxRate / 100);
      
      const itemTotal = subtotalAfterDiscount + taxAmount;

      subtotal += itemSubtotal;
      totalTax += taxAmount;

      return {
        ...item,
        discount_amount: itemDiscount,
        tax_amount: taxAmount,
        total: itemTotal
      };
    });

    // Calculate order-level discount
    let orderDiscountAmount = 0;
    if (orderDiscount) {
      const discountableAmount = subtotal - itemDiscountTotal;
      if (orderDiscount.type === 'percent') {
        orderDiscountAmount = discountableAmount * (orderDiscount.value / 100);
      } else {
        orderDiscountAmount = Math.min(orderDiscount.value, discountableAmount);
      }
    }

    const totalDiscountAmount = itemDiscountTotal + orderDiscountAmount;
    const finalTotal = subtotal - totalDiscountAmount + totalTax;

    set({
      items: updatedItems,
      subtotal,
      itemDiscountTotal,
      orderDiscountAmount,
      totalDiscountAmount,
      taxAmount: totalTax,
      total: Math.max(0, finalTotal)
    });
  },
}));
